'use client';

import { usePathname } from 'next/navigation';
import { useCallback, useEffect, useRef, useState } from 'react';

import type { Auth0LoginMethod } from '@libs/common-next';
import { useLoyaltyContext, usePageLayout } from '@libs/common-next';
import { isRememberMeTrue } from '@libs/core';

import { dataLayerPush } from '../gtm/dataLayerPush';
import { toReportableLoyaltyNumber } from '../gtm/events';
import type { TreatsLoginLocation } from '../gtm/types';
import type { EventTracking } from '../types';

export type Auth0LoginEventParams = {
    loginMethod: Auth0LoginMethod;
    email?: string | null;
    loyaltyId?: number | string | null;
    treatsPoints?: number | null;
    currentTierRank?: number | null;
};

/**
 * Auth0 `login` payload. Reuses the legacy event name and required fields
 * (`email`, `keep_signed_in`) but omits loyalty placeholders — ID creation and
 * member-points fetch are async and must not block a successful authentication.
 */
export function trackAuth0LoginSuccessful({
    loginMethod,
    email,
    loyaltyId,
    treatsPoints,
    currentTierRank,
}: Auth0LoginEventParams) {
    const reportableLoyaltyId = toReportableLoyaltyNumber(loyaltyId);
    const reportableTreatsPoints = toReportableLoyaltyNumber(treatsPoints);
    const reportableTierRank = toReportableLoyaltyNumber(currentTierRank);

    dataLayerPush({
        event: 'login',
        login_status: true,
        login_method: loginMethod,
        email: email || null,
        keep_signed_in: isRememberMeTrue() ? 'yes' : 'no',
        ...(reportableLoyaltyId !== undefined && { loyalty_id: reportableLoyaltyId }),
        ...(reportableTreatsPoints !== undefined && { treats_points: reportableTreatsPoints }),
        ...(reportableTierRank !== undefined && { treats_tier: `tier ${reportableTierRank}` }),
    });
}

export function useLoginEvents() {
    const { user } = usePageLayout();
    const { memberPoints } = useLoyaltyContext();
    const pathname = usePathname();

    const [isLoginAction, setIsLoginAction] = useState<boolean>(false);
    const loginLocation = useRef<TreatsLoginLocation | null>(null);
    const userEmail = useRef<string | null>(null);
    const cbFunction = useRef<() => void>(() => {});

    const trackLoginSuccessful = useCallback(() => {
        dataLayerPush({
            event: 'login',
            login_status: true,
            login_method: loginLocation.current,
            email: userEmail.current,
            keep_signed_in: isRememberMeTrue() ? 'yes' : 'no',
            loyalty_id: user?.loyaltyid,
            treats_points: memberPoints?.availablePoints,
            treats_tier: `tier ${String(memberPoints?.currentTierRank)}`,
        });

        setIsLoginAction(false);
        cbFunction.current?.();
    }, [user?.loyaltyid, memberPoints]);

    async function trackTreatsLogin(pathOverride: string | null, isFirstAutoshipEligible: boolean) {
        const eventData: EventTracking = {
            event: 'TreatsLogin',
            loginLocation: loginLocation.current,
            previousPagePath: pathOverride ?? pathname ?? '/',
            first_autoship_eligible: isFirstAutoshipEligible,
        };

        dataLayerPush(eventData);
    }

    async function trackSuccessSignIn(
        location: TreatsLoginLocation | null,
        isFirstAutoshipEligible: boolean,
        pathOverride: string | null = null,
        email: string | null = null,
        callbackFunction: () => void
    ) {
        loginLocation.current = location;
        userEmail.current = email;
        cbFunction.current = callbackFunction;
        trackTreatsLogin(pathOverride, isFirstAutoshipEligible);

        if (user?.loyaltyid && memberPoints) {
            // if has data, trigger directly (soft login use case)
            trackLoginSuccessful();
        } else {
            setIsLoginAction(true); // enable useEffect to fire login successful
        }
    }

    useEffect(() => {
        if (isLoginAction && user?.loyaltyid && memberPoints && loginLocation.current) {
            trackLoginSuccessful();
        }
    }, [user?.loyaltyid, isLoginAction, memberPoints, trackLoginSuccessful]);

    return {
        trackSuccessSignIn,
        trackLoginSuccessful,
    };
}
